import { Button, Grid, Typography } from '@mui/material'
import UseCaseCard from './UseCaseCard'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueOptionText } from '../../generic/systemValues/systemValueLabel'
import { useEffect, useState } from 'react'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import AddIcon from '@mui/icons-material/Add'
import SmartTextField from '../../generic/smarttextfield/SmartTextField'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import AlertUI from '../../generic/AlertUI'
import { apiGet } from '../../generic/Api_Functions'

const DEFAULT_EDITING_FUNCTION = {
  id: null,
  function: '',
  order: '',
  useCase: null
}

const UseCaseSection = ({ useCaseFunctions, setUseCaseFunctions, editMode }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingFunction, setEditingFunction] = useState(DEFAULT_EDITING_FUNCTION)
  const [selectableUseCases, setSelectableUseCases] = useState([])
  const [selectableFunctions, setSelectableFunctions] = useState([])

  useEffect(() => {
    const fetchSelectables = async () => {
      await apiGet('cellUseCases').then((response) => {
        setSelectableUseCases(response.map((useCase) => ({ id: useCase.uuid, code: useCase.code, name: useCase.name })))
      })
      await apiGet('listFunctions').then((response) => {
        setSelectableFunctions(response)
      })
    }

    fetchSelectables()
  }, [showAddDialog])

  const getNextOrder = () => {
    if (!useCaseFunctions?.length) return 10
    const maxOrder = Math.max(...useCaseFunctions.map((f) => Number(f.order) || 0))
    return Math.ceil(maxOrder / 10) * 10 + 10
  }

  const dialogComponent = (
    <>
      {AlertElement}
      <Grid style={{ display: 'flex', gap: 16, marginTop: '1rem' }}>
        <Grid item xs={2}>
          <SmartTextField
            label={t_('Order')}
            type='number'
            value={editingFunction.order}
            required
            style={{ width: '100%' }}
            onChange={(value) => setEditingFunction((prev) => ({ ...prev, order: value }))}
          />
        </Grid>
        <Grid item xs={6}>
          <SmartSelect
            label={t_('Function Name')}
            value={editingFunction.function}
            selectableOptions={selectableFunctions || []}
            required
            style={{ width: '100%' }}
            onChange={(value) => {
              setEditingFunction((prev) => ({ ...prev, function: value }))
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <SmartSelect
            label={t_('Use Case')}
            value={editingFunction.useCase?.name || ''}
            selectableOptions={selectableUseCases?.map((useCase) => useCase.name)}
            optionLabelTransform={(name) => systemValueOptionText(t_, 'cell_use_case', selectableUseCases, name)}
            required
            style={{ width: '100%' }}
            onChange={(value) => {
              const selectedUseCase = selectableUseCases.find((useCase) => useCase.name === value)
              setEditingFunction((prev) => ({ ...prev, useCase: selectedUseCase || null }))
            }}
          />
        </Grid>
      </Grid>
    </>
  )

  const handleOnAccept = () => {
    if (!editingFunction.function || !editingFunction.useCase || !editingFunction.order) {
      showAlert({ severity: 'error', message: t_('Please fill in all required fields') })
      return
    }

    const orderNum = Number(editingFunction.order)
    const isDuplicate = useCaseFunctions?.some((f) => Number(f.order) === orderNum)
    if (isDuplicate) {
      showAlert({ severity: 'error', message: t_('Order value already exists') })
      return
    }

    setUseCaseFunctions([...(useCaseFunctions || []), { ...editingFunction, id: crypto.randomUUID() }])
    setEditingFunction(DEFAULT_EDITING_FUNCTION)
    setShowAddDialog(false)
  }

  return (
    <>
      <Button
        sx={{ borderColor: 'transparent', mb: 2 }}
        color='primary'
        variant='outlined'
        disabled={!editMode}
        onClick={() => {
          setEditingFunction({ ...DEFAULT_EDITING_FUNCTION, order: getNextOrder() })
          setShowAddDialog(true)
        }}
      >
        {t_('Add Record')}
      </Button>
      <SmartDialog
        title={{ icon: <AddIcon fontSize='large' />, render: <Typography variant='h4'>{t_('Add use case functions')}</Typography> }}
        setOpen={showAddDialog}
        acceptCallback={() => { handleOnAccept() }}
        cancelCallback={() => { setEditingFunction(DEFAULT_EDITING_FUNCTION); setShowAddDialog(false) }}
        renderComponent={dialogComponent}
        paperWidth='40vw'
      />
      <Grid container spacing={2}>
        {[...(useCaseFunctions || [])].sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)).map((f) => (
          <UseCaseCard
            key={f.id}
            useCaseFunction={f}
            editMode={editMode}
            onUpdateOrder={(newOrder) => {
              const orderNum = Number(newOrder)
              const isDuplicate = useCaseFunctions.some((uf) => uf.id !== f.id && Number(uf.order) === orderNum)
              if (isDuplicate) return { error: t_('Order value already exists') }
              setUseCaseFunctions(useCaseFunctions.map((uf) => uf.id === f.id ? { ...uf, order: orderNum } : uf))
              return { error: null }
            }}
            onDelete={(id) => {
              setUseCaseFunctions(useCaseFunctions.filter((uf) => uf.id !== id))
            }}
          />
        ))}
      </Grid>
    </>
  )
}

export default UseCaseSection
